// Suite app switcher (user request 19.10.2026; reference implementation for every program of the suite – see
// README.md in this folder). A click on the app's own logo unfolds the logos of the other suite programs the person
// may open; a click on one opens that program. Nothing here is named on screen except the programs themselves.
//
// Self-contained: this file + suite-select.css. The program supplies three things (props): `loadApps` (its BFF call
// to RimoID's suite list), the logo element (`anchor`, a CSS selector) and `openApp` (browser tab / desktop app).
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './suite-select.css';

export interface SuiteApp { key: string; name: string; url: string; accent?: string | null; icon?: string | null; desktopScheme?: string | null; role?: string | null;
  /** AE: the person's devices where this program's desktop app runs (RimoID, desktop tokens only). */
  devices?: string[] }

export interface SuiteSelectProps {
  /** Key of this program (left out of the list), e.g. "rimocompta". */
  current: string;
  /** CSS selector of the logo element the switcher attaches to (the program's top-left logo). */
  anchor: string;
  /** The programs the person may open, in RimoID's order (the program's BFF → RimoID `GET /api/v1/suite/apps`). */
  loadApps: () => Promise<SuiteApp[]>;
  /** Opens a program (browser: same tab; desktop: its desktop app or the system browser). */
  openApp: (app: SuiteApp) => void | Promise<void>;
  /** Logo of a program: its product hexagon from brand/ (`<key>-favicon.svg`, like the app's own logo). Default `assets/suite/<key>.svg`. */
  iconUrl?: (key: string) => string;
  /** Reload trigger (token refreshed, rights changed …): called with `reload`, returns the unsubscribe. */
  subscribe?: (reload: () => void) => () => void;
  /** AE: whether the program's desktop app runs on this computer – marked with a dot, title + `runningLabel` (e.g. „In der App öffnen“). */
  running?: (app: SuiteApp) => boolean;
  runningLabel?: string;
}

/** The programs to show: all but this one (pure, for tests). An empty result means the logo stays a plain logo. */
export const otherApps = (list: readonly SuiteApp[], current: string) => list.filter((a) => a.key !== current);
/** AE: whether the program's desktop app runs on this device (pure, for `running`). */
export const runsOn = (app: SuiteApp, deviceId: string | null | undefined) => !!deviceId && !!app.devices?.includes(deviceId);

const STAGGER_MS = 40, OPEN_MS = 420, CLOSE_MS = 200;

export function SuiteSelect({ current, anchor, loadApps, openApp, iconUrl = (k) => `assets/suite/${k}.svg`, subscribe, running, runningLabel }: SuiteSelectProps) {
  const [apps, setApps] = useState<SuiteApp[]>([]);
  const [phase, setPhase] = useState<'closed' | 'open' | 'closing'>('closed');
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [logo, setLogo] = useState<HTMLElement | null>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const list = otherApps(apps, current);

  // The list: now and whenever the program says so (new token, changed rights).
  const reload = useCallback(() => { loadApps().then((l) => setApps(Array.isArray(l) ? l : []), () => setApps([])); }, [loadApps]);
  useEffect(() => { reload(); return subscribe?.(reload); }, [reload, subscribe]);

  // The logo is rendered by the program, maybe later or anew: find it and follow replacements.
  useEffect(() => {
    const find = () => setLogo((cur) => { const el = document.querySelector<HTMLElement>(anchor); return el === cur ? cur : el; });
    find();
    const mo = new MutationObserver(find);
    mo.observe(document.body, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, [anchor]);

  const place = useCallback(() => {
    if (!logo) return;
    const r = logo.getBoundingClientRect();
    setPos({ left: Math.round(r.right + 12), top: Math.round(r.top + r.height / 2) });
  }, [logo]);

  const close = useCallback(() => {
    setPhase((p) => (p === 'open' ? 'closing' : p));
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setPhase('closed'), CLOSE_MS + list.length * STAGGER_MS);
  }, [list.length]);
  const toggle = useCallback(() => {
    if (!list.length) return; // no other program: the logo stays a plain logo
    if (phase === 'open') return close();
    if (timer.current) clearTimeout(timer.current);
    place(); setPhase('open');
  }, [list.length, phase, close, place]);

  // The logo as the trigger (only while there is something to open).
  useEffect(() => {
    if (!logo) return;
    const active = list.length > 0;
    logo.style.cursor = active ? 'pointer' : '';
    if (active) { logo.setAttribute('role', 'button'); logo.setAttribute('tabindex', '0'); logo.setAttribute('aria-haspopup', 'menu'); logo.setAttribute('aria-expanded', String(phase === 'open')); }
    else { for (const a of ['role', 'tabindex', 'aria-haspopup', 'aria-expanded']) logo.removeAttribute(a); }
    const onClick = (e: Event) => { if (!active) return; e.preventDefault(); e.stopPropagation(); toggle(); };
    const onKey = (e: KeyboardEvent) => { if (active && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggle(); } };
    logo.addEventListener('click', onClick);
    logo.addEventListener('keydown', onKey);
    return () => { logo.removeEventListener('click', onClick); logo.removeEventListener('keydown', onKey); };
  }, [logo, list.length, phase, toggle]);

  // Closing: click outside, Escape; the position follows the window.
  useEffect(() => {
    if (phase !== 'open') return;
    const down = (e: MouseEvent) => { const t = e.target as Node; if (!panel.current?.contains(t) && !logo?.contains(t)) close(); };
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') { close(); logo?.focus(); } };
    document.addEventListener('mousedown', down, true);
    document.addEventListener('keydown', key);
    window.addEventListener('resize', place);
    return () => { document.removeEventListener('mousedown', down, true); document.removeEventListener('keydown', key); window.removeEventListener('resize', place); };
  }, [phase, logo, close, place]);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  if (phase === 'closed' || !list.length) return null;
  const n = list.length;
  return createPortal(
    <div ref={panel} className={'rss-panel' + (phase === 'closing' ? ' rss-closing' : '')} role="menu" style={{ left: pos.left, top: pos.top }}>
      {list.map((a, i) => { const on = !!running?.(a), label = on && runningLabel ? `${a.name} · ${runningLabel}` : a.name; return (
        <button key={a.key} type="button" role="menuitem" className={'rss-app' + (on ? ' rss-running' : '')} title={label} aria-label={label}
          style={{ animationDelay: `${(phase === 'closing' ? n - 1 - i : i) * STAGGER_MS}ms`, animationDuration: `${phase === 'closing' ? CLOSE_MS : OPEN_MS}ms` }}
          onClick={() => { close(); void openApp(a); }}>
          <img src={iconUrl(a.key)} alt="" draggable={false} />
        </button>
      ); })}
    </div>,
    document.body,
  );
}
